import type { UIMessage } from "ai";
import { readResultId } from "playground/agents/file-tools";

export interface TaskRead {
  path: string;
  windows: string[];
  revision?: string;
}

function readWindow(path: string, offset?: number, range?: number): string {
  if (offset === undefined && range === undefined) return "Whole file";
  const pdf = path.toLowerCase().endsWith(".pdf");
  const unit = pdf ? "Page" : "Line";
  const start = (offset ?? 0) + 1;
  if (range === undefined) return `${unit} ${start} onward`;
  if (range === 1) return `${unit} ${start}`;
  return `${unit} ${start}–${start + range - 1}`;
}

function requestedWindow(input: unknown): {
  offset?: number;
  range?: number;
  region: boolean;
} {
  const request = input as {
    offset?: unknown;
    range?: unknown;
    region?: unknown;
  } | null;
  return {
    offset: typeof request?.offset === "number" ? request.offset : undefined,
    range: typeof request?.range === "number" ? request.range : undefined,
    region: request?.region !== undefined,
  };
}

/** The files the agent actually read, derived from completed tool results. */
export function taskReads(messages: UIMessage[]): TaskRead[] {
  const byPath = new Map<string, TaskRead>();
  for (const message of messages) {
    if (message.role !== "assistant") continue;
    for (const part of message.parts) {
      if (part.type !== "tool-read" || part.state !== "output-available")
        continue;
      const read = readResultId(part.output);
      if (!read) continue;
      const output = part.output as {
        heads?: unknown;
        unchanged?: unknown;
        catchUp?: unknown;
        files?: unknown;
      };
      // An unchanged marker sent no bytes, so it adds no new source window.
      if (output.unchanged === true) continue;
      const request = requestedWindow(part.input);
      const offset = read.window.offset ?? request.offset;
      const requestedRange = read.window.range ?? request.range;
      const isPdf = read.path.toLowerCase().endsWith(".pdf");
      const files = Array.isArray(output.files) ? output.files : [];
      const range =
        isPdf &&
        files.length &&
        (offset !== undefined || requestedRange !== undefined)
          ? files.length
          : request.region
            ? 1
            : requestedRange;
      const heads = Array.isArray(output.heads)
        ? output.heads.filter(
            (head): head is string => typeof head === "string",
          )
        : [];
      const previous = byPath.get(read.path);
      const window =
        output.catchUp === true
          ? "Change patch"
          : request.region
            ? `${readWindow(read.path, offset, 1)} region`
            : readWindow(read.path, offset, range);
      byPath.delete(read.path);
      byPath.set(read.path, {
        path: read.path,
        windows: previous?.windows.includes(window)
          ? previous.windows
          : [...(previous?.windows ?? []), window],
        revision: heads.length ? heads[0]?.slice(0, 8) : previous?.revision,
      });
    }
  }
  return [...byPath.values()].reverse();
}
